/**
 * Runtime modal ve menu yardimcilari
 */

let lastFocusedElement: HTMLElement | null = null;

function setupModalKeyboardTrap(modal: HTMLElement, closeModalFn: (modalId: string) => void): void {
    const focusableElements = modal.querySelectorAll(
        'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstEl = focusableElements[0] as HTMLElement | undefined;
    const lastEl = focusableElements[focusableElements.length - 1] as HTMLElement | undefined;

    function handleTabKey(event: KeyboardEvent): void {
        if (event.key === 'Tab' && firstEl && lastEl) {
            if (event.shiftKey && document.activeElement === firstEl) {
                event.preventDefault();
                lastEl.focus();
            } else if (!event.shiftKey && document.activeElement === lastEl) {
                event.preventDefault();
                firstEl.focus();
            }
        }

        if (event.key === 'Escape') {
            closeModalFn(modal.id);
        }
    }

    const modalWithHandler = modal as HTMLElement & { _keyHandler?: (event: KeyboardEvent) => void };
    if (modalWithHandler._keyHandler) {
        modal.removeEventListener('keydown', modalWithHandler._keyHandler);
    }

    modalWithHandler._keyHandler = handleTabKey;
    modal.addEventListener('keydown', handleTabKey);
}

export function openModal(modalId: string): void {
    const modal = document.getElementById(modalId) as HTMLElement | null;
    if (!modal) return;

    lastFocusedElement = document.activeElement as HTMLElement | null;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    const titleEl = modal.querySelector('.modal-header h3') as HTMLElement | null;
    if (titleEl) {
        if (!titleEl.id) {
            titleEl.id = `${modalId}-title`;
        }
        modal.setAttribute('aria-labelledby', titleEl.id);
    }

    modal.style.display = 'flex';
    modal.classList.add('active');
    document.body.style.overflow = 'hidden';

    requestAnimationFrame(() => {
        const firstFocusable = modal.querySelector(
            'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
        ) as HTMLElement | null;
        firstFocusable?.focus();
    });

    setupModalKeyboardTrap(modal, closeModal);
}

export function closeModal(modalId: string): void {
    const modal = document.getElementById(modalId) as HTMLElement | null;
    if (!modal) return;

    modal.classList.remove('active');
    modal.style.display = 'none';
    document.body.style.overflow = '';
    modal.removeAttribute('aria-modal');
    modal.removeAttribute('aria-labelledby');

    const form = modal.querySelector('form') as HTMLFormElement | null;
    form?.reset();

    modal.querySelectorAll('.field-error').forEach(element => element.remove());
    modal.querySelectorAll('.input-error').forEach(element => element.classList.remove('input-error'));

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        lastFocusedElement.focus();
        lastFocusedElement = null;
    }
}

export function closeAllModals(): void {
    document.querySelectorAll('.modal.active').forEach(modal => closeModal((modal as HTMLElement).id));
}

export function setMenuState(menuId: string, toggleId: string, isOpen: boolean): void {
    const menu = document.getElementById(menuId);
    const toggle = document.getElementById(toggleId);
    if (!menu || !toggle) return;

    menu.classList.toggle('active', isOpen);
    toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
}
